import { useCallback, useEffect, useRef, useState } from 'react';
import { druckAdresseHolen, inSafariOeffnen, type DruckArt } from '@/lib/iosDruck';
import { Spinner } from '@/components/ui/Spinner';

// Ersatz für window.print() in der iPhone-Home-Bildschirm-App
// (lib/iosDruck.ts): Druckseite mit einem 10-Minuten-Zeichen in Safari
// öffnen. Der erste Versuch läuft von selbst; der Knopf holt bei jedem
// Tipp ein frisches Zeichen, damit ein abgelaufenes nie im Weg steht.

type Zustand =
  | { art: 'bereit' }
  | { art: 'laedt' }
  | { art: 'offen'; adresse: string }
  | { art: 'fehler' };

// Merker nur für diese Sperre (keine sensiblen Daten). sessionStorage
// überlebt das Neuladen der App, nicht aber ihr Schließen.
const SPERRE_MS = 60_000;
function merkSchluessel(art: DruckArt, bestellnr: string): string {
  return `safari-druck:${art}:${bestellnr}`;
}
function kuerzlichGeoeffnet(art: DruckArt, bestellnr: string): boolean {
  try {
    const t = Number(sessionStorage.getItem(merkSchluessel(art, bestellnr)) ?? 0);
    return Date.now() - t < SPERRE_MS;
  } catch {
    return false;
  }
}
function merkeGeoeffnet(art: DruckArt, bestellnr: string): void {
  try {
    sessionStorage.setItem(merkSchluessel(art, bestellnr), String(Date.now()));
  } catch {
    // ohne Speicher eben ohne Sperre
  }
}

export function SafariUebergabe({
  bestellnr,
  druckArt,
  variante,
  onZurueck,
  onUebergeben
}: {
  bestellnr: string;
  druckArt: DruckArt;
  variante?: string;
  onZurueck: () => void;
  /**
   * Safari hat übernommen (die App ist in den Hintergrund gegangen).
   * Die Route soll dann verschwinden: Kehrt man in die App zurück, lädt
   * iOS sie oft neu — und eine stehengebliebene Druckroute öffnete
   * Safari sofort ein zweites Mal (Befund 28.10.2026, „Zurück zur
   * Bestellung öffnet Safari nochmal").
   */
  onUebergeben?: () => void;
}) {
  const [zustand, setZustand] = useState<Zustand>({ art: 'laedt' });
  const [kopiert, setKopiert] = useState(false);
  const gestartet = useRef(false);

  const oeffnen = useCallback(async () => {
    setZustand({ art: 'laedt' });
    setKopiert(false);
    try {
      const adresse = await druckAdresseHolen(bestellnr, druckArt, variante);
      setZustand({ art: 'offen', adresse });
      if (onUebergeben) {
        // Erst wenn die App wirklich in den Hintergrund geht, hat Safari
        // übernommen. Bleibt sie sichtbar, ging Safari nicht auf — dann
        // bleiben „In Safari öffnen" und „Link kopieren" stehen.
        const weg = (): void => {
          if (!document.hidden) return;
          document.removeEventListener('visibilitychange', weg);
          onUebergeben();
        };
        document.addEventListener('visibilitychange', weg);
      }
      inSafariOeffnen(adresse);
    } catch {
      setZustand({ art: 'fehler' });
    }
  }, [bestellnr, druckArt, variante, onUebergeben]);

  useEffect(() => {
    if (gestartet.current) return;
    gestartet.current = true;
    // Von selbst nur einmal je Bestellung und Minute. Lädt iOS die App
    // beim Zurückwechseln neu, hängt sich die Route erneut ein — ohne
    // diese Sperre ginge Safari sofort ein zweites Mal auf. Der Knopf
    // „In Safari öffnen" bleibt davon unberührt.
    if (kuerzlichGeoeffnet(druckArt, bestellnr)) {
      setZustand({ art: 'bereit' });
      return;
    }
    merkeGeoeffnet(druckArt, bestellnr);
    void oeffnen();
  }, [oeffnen, druckArt, bestellnr]);

  async function kopieren(adresse: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(adresse);
      setKopiert(true);
    } catch {
      setKopiert(false);
    }
  }

  const was = druckArt === 'lieferschein' ? 'Der Lieferschein' : 'Der Zettel';

  return (
    <div className="flex min-h-screen flex-col items-center bg-cream p-6 text-ink">
      <div className="w-full max-w-md space-y-4">
        <h1 className="font-serif text-xl">Drucken über Safari</h1>
        <p className="text-sm leading-relaxed">
          Aus der App auf dem Home-Bildschirm kann das iPhone nicht mehr selbst drucken. {was}{' '}
          öffnet sich deshalb in Safari, dort erscheint der Druckdialog. Danach einfach zur App
          zurückwechseln.
        </p>

        {zustand.art === 'laedt' && (
          <div className="flex items-center gap-2 text-sm">
            <Spinner className="h-5 w-5" /> Safari wird geöffnet …
          </div>
        )}
        {zustand.art === 'fehler' && (
          <p className="text-sm text-coral">
            Der Druck-Link konnte nicht erstellt werden. Bitte noch einmal versuchen.
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => void oeffnen()}
            disabled={zustand.art === 'laedt'}
            className="min-h-[44px] rounded-md bg-akzent px-4 text-sm text-cream disabled:opacity-50"
          >
            In Safari öffnen
          </button>
          <button
            type="button"
            onClick={onZurueck}
            className="min-h-[44px] rounded-md border border-line px-4 text-sm"
          >
            Zurück zur Bestellung
          </button>
        </div>

        {zustand.art === 'offen' && (
          <div className="space-y-2 border-t border-line pt-4 text-sm">
            <p>Safari geht nicht auf? Dann den Link kopieren und in Safari einfügen.</p>
            <button
              type="button"
              onClick={() => void kopieren(zustand.adresse)}
              className="min-h-[44px] rounded-md border border-line px-4 text-sm"
            >
              {kopiert ? 'Link kopiert' : 'Link kopieren'}
            </button>
            <p className="text-xs text-ink-faint">Der Link gilt 10 Minuten und nur für diese Bestellung.</p>
          </div>
        )}
      </div>
    </div>
  );
}
